import { useCallback, useContext } from 'react';
import { AppCheckSdkContext } from 'reactfire';
import { useCsrf } from '~/core/firebase/hooks/use-csrf-token';
import { getAnalyticsConsentRequestHeaders } from '~/core/consent/analytics-request';
import {
  isCsrfFailureResponse,
  throwIfProtectedApiResponseStartsRecovery,
} from '~/core/firebase/client-auth-recovery';

const FIREBASE_APP_CHECK_HEADER = 'X-Firebase-AppCheck';
const CSRF_TOKEN_HEADER = 'x-csrf-token';

/**
 * @name useApiRequest
 * @description A hook to make API requests
 * 1. By default, it will use the `POST` method and will send the payload as
 * JSON
 * 2. Also, it will automatically add the CSRF token to the request headers and
 * the App Check token if the SDK is initialized
 * 3. Automatically retries on CSRF validation failure by refreshing the token
 */
export function useApiRequest<Resp = unknown, Body = void>() {
  const getSecurityHeaders = useGetSecurityHeaders();

  return useCallback(
    async (params: {
      path: string;
      body?: Body;
      method?: HttpMethod;
      headers?: StringObject;
    }) => {
      const securityHeaders = await getSecurityHeaders();
      const payload = JSON.stringify(params.body);

      const requestHeaders: StringObject = {
        ...(params.headers ?? {}),
      };

      // add the AppCheck token to the request headers if it exists
      if (securityHeaders.appCheckToken) {
        requestHeaders[FIREBASE_APP_CHECK_HEADER] =
          securityHeaders.appCheckToken;
      }

      // add the CSRF token to the request headers if it exists
      if (securityHeaders.csrfToken) {
        requestHeaders[CSRF_TOKEN_HEADER] = securityHeaders.csrfToken;
      }

      // execute the fetch request with CSRF retry
      return executeFetchRequestWithRetry<Resp>(
        params.path,
        payload,
        params.method,
        requestHeaders,
        securityHeaders.refreshCsrfToken,
      );
    },
    [getSecurityHeaders],
  );
}

async function executeFetchRequest<Resp = unknown>(
  url: string,
  payload: string,
  method = 'POST',
  headers?: StringObject,
): Promise<{ response: Response; data?: Resp }> {
  const options: RequestInit = {
    method,
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
      ...(headers ?? {}),
      ...getAnalyticsConsentRequestHeaders(),
    },
  };

  const methodsSupportingBody: HttpMethod[] = [
    'POST',
    'PUT',
    'PATCH',
    'DELETE',
  ];
  const supportsBody = methodsSupportingBody.includes(method as HttpMethod);

  if (payload && supportsBody) {
    options.body = payload;
  }

  const response = await fetch(url, options);

  // if the response is a redirect, we redirect the user
  // to the new location
  if (response.redirected) {
    window.location.assign(response.url);
    return { response, data: undefined as Resp };
  }

  if (response.ok) {
    const data = (await response.json()) as Resp;
    return { response, data };
  }

  return { response };
}

async function executeFetchRequestWithRetry<Resp = unknown>(
  url: string,
  payload: string,
  method = 'POST',
  headers?: StringObject,
  refreshCsrfToken?: () => Promise<string | null>,
): Promise<Resp> {
  try {
    const result = await executeFetchRequest<Resp>(
      url,
      payload,
      method,
      headers,
    );

    // If CSRF validation failed and we have a refresh function, try again
    await throwIfProtectedApiResponseStartsRecovery(
      result.response,
      'useApiRequest',
    );

    if (
      result.response.status === 401 &&
      refreshCsrfToken &&
      (await isCsrfFailureResponse(result.response))
    ) {
      const newToken = await refreshCsrfToken();
      if (newToken) {
        const retryHeaders = { ...headers, [CSRF_TOKEN_HEADER]: newToken };
        const retryResult = await executeFetchRequest<Resp>(
          url,
          payload,
          method,
          retryHeaders,
        );

        await throwIfProtectedApiResponseStartsRecovery(
          retryResult.response,
          'useApiRequest.retry',
        );

        if (retryResult.response.ok && retryResult.data !== undefined) {
          return retryResult.data;
        }

        if (!retryResult.response.ok) {
          return Promise.reject(
            await retryResult.response.json().catch(() => ({})),
          );
        }
      }
    }

    if (result.response.ok && result.data !== undefined) {
      return result.data;
    }

    return Promise.reject(await result.response.json().catch(() => ({})));
  } catch (error) {
    return Promise.reject(error);
  }
}

function useGetAppCheckToken() {
  // instead of using useAppCheck()
  // we manually request the SDK
  // because we *may not have initialized it*
  const sdk = useContext(AppCheckSdkContext);

  return useCallback(async () => {
    try {
      // if the SDK does not exist, we cannot generate a token
      // so we return undefined
      if (!sdk) {
        return;
      }

      const forceRefresh = false;
      const { getToken } = await import('firebase/app-check');
      const { token } = await getToken(sdk, forceRefresh);

      return token;
    } catch (e) {
      return;
    }
  }, [sdk]);
}

function useGetSecurityHeaders() {
  const { token: csrfToken, refreshToken } = useCsrf();
  const getAppCheckToken = useGetAppCheckToken();

  return useCallback(async () => {
    const appCheckToken = await getAppCheckToken();

    return {
      csrfToken,
      appCheckToken,
      refreshCsrfToken: refreshToken,
    };
  }, [csrfToken, refreshToken, getAppCheckToken]);
}
